import {
  Map as MaplibreMap,
  CustomLayerInterface,
  MercatorCoordinate,
  LngLat,
} from 'maplibre-gl'
import * as THREE from 'three'
import {Raycaster} from 'three'
import {metersBetweenMercator} from '../mapUtils'
import {AdvisorObject} from './AdvisorObject'
import {IThreeJsLayer} from './interfaces/IThreeJsLayer'
import {IDroneUpdatable, IUniformsUpdatable} from './interfaces/IObjectLoader'

const SCENE_ROTATE_X = Math.PI / 2

const LIGHTING = {
  directional: [
    {pos: [50, 70, -30] as const, intensity: 2},
    {pos: [-50, 70, 30] as const, intensity: 2},
    {pos: [0, -50, 50] as const, intensity: 1.5},
  ],
  ambient: {color: 0xffffff, intensity: 1.5},
  hemisphere: {sky: 0xffffff, ground: 0x444444, intensity: 1.5},
} as const

interface ObjectData {
  object: THREE.Object3D
  lngLat: LngLat
  altitude: number
}

export class ThreeJsLayer implements CustomLayerInterface, IThreeJsLayer {
  id: string
  type: 'custom' = 'custom'
  renderingMode: '3d' = '3d'

  private map!: MaplibreMap
  private camera!: THREE.PerspectiveCamera
  private scene!: THREE.Scene
  private renderer!: THREE.WebGLRenderer
  private objects: globalThis.Map<string, ObjectData> = new globalThis.Map()
  private sceneOrigin: LngLat | null = null
  private raycaster: Raycaster = new Raycaster()
  private lastProjectionMatrix: THREE.Matrix4 | null = null
  private orbLoader: IUniformsUpdatable | null = null
  private billboardLoader: IUniformsUpdatable | null = null
  private droneLoader: IDroneUpdatable | null = null

  constructor(id: string = 'threejs-layer') {
    this.id = id
  }

  initOrbLoader(orbLoader: IUniformsUpdatable): void {
    this.orbLoader = orbLoader
  }

  initBillboardLoader(billboardLoader: IUniformsUpdatable): void {
    this.billboardLoader = billboardLoader
  }

  initDroneLoader(droneLoader: IDroneUpdatable): void {
    this.droneLoader = droneLoader
  }

  onAdd(map: MaplibreMap, gl: WebGLRenderingContext) {
    this.map = map
    const canvas = map.getCanvas()

    this.camera = new THREE.PerspectiveCamera()
    this.camera.userData.canvas = canvas

    this.scene = new THREE.Scene()
    this.scene.rotateX(SCENE_ROTATE_X)
    this.scene.scale.multiply(new THREE.Vector3(1, 1, -1))

    for (const light of LIGHTING.directional) {
      const directional = new THREE.DirectionalLight(0xffffff, light.intensity)
      directional.position
        .set(light.pos[0], light.pos[1], light.pos[2])
        .normalize()
      this.scene.add(directional)
    }
    this.scene.add(
      new THREE.AmbientLight(LIGHTING.ambient.color, LIGHTING.ambient.intensity)
    )
    this.scene.add(
      new THREE.HemisphereLight(
        LIGHTING.hemisphere.sky,
        LIGHTING.hemisphere.ground,
        LIGHTING.hemisphere.intensity
      )
    )

    this.renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      context: gl,
      antialias: true,
    })
    this.renderer.autoClear = false
    this.renderer.setSize(canvas.width, canvas.height)
  }

  private updateSceneOrigin() {
    const center = this.map.getCenter()
    this.sceneOrigin = new LngLat(center.lng, center.lat)
  }

  private updateObjectPositions() {
    if (!this.sceneOrigin) return

    const originMercator = MercatorCoordinate.fromLngLat(this.sceneOrigin)

    this.objects.forEach((data) => {
      const objMercator = MercatorCoordinate.fromLngLat(
        data.lngLat,
        data.altitude
      )
      const {dEastMeter, dNorthMeter} = metersBetweenMercator(
        originMercator,
        objMercator
      )

      data.object.position.set(dEastMeter, data.altitude, dNorthMeter)
    })
  }

  render(gl: WebGLRenderingContext, args: unknown) {
    this.updateSceneOrigin()
    const currentTimeEarly = performance.now() * 0.001
    if (this.droneLoader) {
      this.droneLoader.update(currentTimeEarly)
    }
    this.updateObjectPositions()

    const cameraPosition = new THREE.Vector3(0, 0, 0)
    if (this.camera && this.lastProjectionMatrix) {
      const camInverseProjection = new THREE.Matrix4()
        .copy(this.lastProjectionMatrix)
        .invert()
      cameraPosition.applyMatrix4(camInverseProjection)
    }

    const cameraWorldPos = new THREE.Vector3()
    this.camera.getWorldPosition(cameraWorldPos)

    const originElevation = 0
    const originMercator = MercatorCoordinate.fromLngLat(
      this.sceneOrigin!,
      originElevation
    )

    const sceneTransform = {
      translateX: originMercator.x,
      translateY: originMercator.y,
      translateZ: originMercator.z || 0,
      scale: originMercator.meterInMercatorCoordinateUnits(),
    }
    const projectionArgs = args as {
      defaultProjectionData?: {mainMatrix?: number[]}
    }
    const mainMatrix = projectionArgs.defaultProjectionData?.mainMatrix
    if (!mainMatrix) return

    const m = new THREE.Matrix4().fromArray(mainMatrix)
    const l = new THREE.Matrix4()
      .makeTranslation(
        sceneTransform.translateX,
        sceneTransform.translateY,
        sceneTransform.translateZ
      )
      .scale(
        new THREE.Vector3(
          sceneTransform.scale,
          -sceneTransform.scale,
          sceneTransform.scale
        )
      )

    this.camera.projectionMatrix = m.multiply(l)
    this.lastProjectionMatrix = this.camera.projectionMatrix.clone()

    const currentTime = performance.now() * 0.001
    const deltaTime = 0.016

    if (this.orbLoader) {
      this.orbLoader.updateUniforms(currentTime)
    }
    if (this.billboardLoader) {
      this.billboardLoader.updateUniforms(currentTime)
    }
    this.objects.forEach((data) => {
      if (data.object instanceof AdvisorObject) {
        data.object.update(cameraPosition, currentTime)
      }
      const spinSpeed = data.object.userData?.spinSpeed
      if (spinSpeed) {
        data.object.rotation.y = currentTime * spinSpeed
      }
    })

    this.renderer.resetState()

    this.renderer.render(this.scene, this.camera)
    this.map.triggerRepaint()
  }

  addObject(
    id: string,
    object: THREE.Object3D,
    lngLat: [number, number],
    altitude: number = 0,
    scale: number = 1
  ): THREE.Object3D {
    const location = new LngLat(lngLat[0], lngLat[1])

    const data: ObjectData = {object, lngLat: location, altitude}
    this.objects.set(id, data)

    this.scene.add(object)

    return object
  }

  removeObject(idOrObject: string | THREE.Object3D) {
    let idToRemove: string | null = null
    let objectToRemove: THREE.Object3D | null = null

    if (typeof idOrObject === 'string') {
      const data = this.objects.get(idOrObject)
      if (data) {
        idToRemove = idOrObject
        objectToRemove = data.object
      }
    } else {
      this.objects.forEach((data, id) => {
        if (data.object === idOrObject) {
          idToRemove = id
          objectToRemove = data.object
        }
      })
    }

    if (idToRemove && objectToRemove) {
      this.scene.remove(objectToRemove)
      this.objects.delete(idToRemove)

      if (objectToRemove instanceof AdvisorObject) {
        objectToRemove.dispose()
      } else {
        objectToRemove.traverse((child) => {
          const mesh = child as THREE.Mesh
          if (mesh.isMesh) {
            mesh.geometry?.dispose()
            if (Array.isArray(mesh.material)) {
              mesh.material.forEach((mat) => mat.dispose())
            } else if (mesh.material) {
              mesh.material.dispose()
            }
          }
        })
      }
    }
  }

  updateObjectPosition(object: THREE.Object3D, lngLat: [number, number]) {
    this.objects.forEach((data) => {
      if (data.object === object) {
        data.lngLat = new LngLat(lngLat[0], lngLat[1])
      }
    })
  }

  updateObjectPositionById(id: string, lngLat: [number, number]) {
    const data = this.objects.get(id)
    if (data) {
      data.lngLat = new LngLat(lngLat[0], lngLat[1])
    }
  }

  addRawObject(object: THREE.Object3D): void {
    this.scene.add(object)
  }

  removeRawObject(object: THREE.Object3D): void {
    this.scene.remove(object)
    object.traverse((child) => {
      const mesh = child as THREE.Mesh
      if (mesh.isMesh) {
        mesh.geometry?.dispose()
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((m) => m.dispose())
        } else {
          mesh.material?.dispose()
        }
      }
    })
  }

  getSceneOrigin(): [number, number] | null {
    return this.sceneOrigin
      ? [this.sceneOrigin.lng, this.sceneOrigin.lat]
      : null
  }

  getObject(id: string): THREE.Object3D | undefined {
    return this.objects.get(id)?.object
  }

  clearAll() {
    this.objects.forEach((_, id) => this.removeObject(id))
  }

  addHalfSphere(
    id: string,
    lngLat: [number, number],
    radius: number = 1500,
    color: number = 0xff9326,
    altitude: number = 0,
    useShader: boolean = true
  ): THREE.Object3D {
    const advisorObject = new AdvisorObject(radius, color, useShader)
    const domeAltitude = altitude - radius * 0.5
    return this.addObject(id, advisorObject, lngLat, domeAltitude, 1)
  }

  raycast(
    x: number,
    y: number,
    objectsToCheck: THREE.Object3D[]
  ): THREE.Intersection[] {
    if (
      !this.camera ||
      !this.map ||
      !this.sceneOrigin ||
      !this.lastProjectionMatrix
    ) {
      return []
    }

    this.updateSceneOrigin()
    this.updateObjectPositions()

    const canvas = this.map.getCanvas()
    const rect = canvas.getBoundingClientRect()

    const canvasX = x - rect.left
    const canvasY = y - rect.top

    if (
      canvasX < 0 ||
      canvasX > rect.width ||
      canvasY < 0 ||
      canvasY > rect.height
    ) {
      return []
    }

    const mouse = new THREE.Vector2()
    mouse.x = (canvasX / rect.width) * 2 - 1
    mouse.y = -((canvasY / rect.height) * 2 - 1)

    const camInverseProjection = new THREE.Matrix4()
      .copy(this.lastProjectionMatrix)
      .invert()
    this.scene.updateMatrixWorld(true)

    const cameraPosition = new THREE.Vector3(0, 0, 0).applyMatrix4(
      camInverseProjection
    )
    const mousePosition = new THREE.Vector3(mouse.x, mouse.y, 1).applyMatrix4(
      camInverseProjection
    )
    const viewDirection = mousePosition.clone().sub(cameraPosition).normalize()

    this.raycaster.set(cameraPosition, viewDirection)
    const intersections = this.raycaster.intersectObjects(objectsToCheck, true)

    return intersections.sort((a, b) => a.distance - b.distance)
  }
}
